import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  addDays,
  addWeeks,
  eachDayOfInterval,
  endOfWeek,
  getMonth,
  isBefore,
  isSameDay,
  isAfter,
  startOfWeek,
  subMonths,
} from 'date-fns';
import { IntlShape } from 'react-intl';
import type { DateValue } from '../types';
import type { OnValidateCallbackType } from '../validation';
import { WEEKLY_START_DAY_OPTIONS } from '../types';
import { getNextState, isValidDate, type ActiveDateField } from '../utils/dateRange';
import { isDateInView } from '../utils/rangeDisplay';
import { WEEKLY_MAX_RANGE_WEEKS } from '../validation';
import type { ValidState } from './useDateRangeState';

export interface WeekRangeStateOptions {
  value: DateValue;
  onChange: (value: DateValue) => void;
  onCancel: () => void;
  today: Date;
  disabledDates: (date: Date) => boolean;
  onValidate?: OnValidateCallbackType;
  isComparison?: boolean;
  mainRangeDuration?: number;
  intl: IntlShape;
  hideButtons?: boolean;
}

export interface WeekHoverModifiers {
  weekHoverMiddle: Date[];
  weekHoverEdge: Date[];
}

export interface WeekRangeState {
  draft: DateValue;
  valid: ValidState;
  activeDateField: ActiveDateField;
  setActiveDateField: (field: ActiveDateField) => void;
  calendarMonth: Date;
  weekHoverModifiers: WeekHoverModifiers;
  handleApply: () => void;
  handleCancel: () => void;
  handlePresetChange: (from: Date | undefined, to: Date | undefined, presetValue: string, labelKey: string) => void;
  handleDayClick: (proposedDate: Date) => void;
  handleDayHover: (date: Date) => void;
  handleCustomInputChange: (from: Date | undefined, to: Date | undefined) => void;
  weeklyLimitAwareDisabledDates: (date: Date) => boolean;
}

function getInitialMonth(from: Date | undefined, to: Date | undefined, today: Date): Date {
  let month = subMonths(today, 1);
  if (to && isValidDate(to)) month = subMonths(to, 1);
  if (from && isValidDate(from) && !isDateInView(from, month)) month = from;
  if (getMonth(month) === getMonth(today)) month = subMonths(month, 1);
  return month;
}

const makeValidationHandler =
  (setValid: (v: ValidState) => void, onValidate: OnValidateCallbackType | undefined, _intl: IntlShape) =>
  ({ from, to }: { from: Date; to: Date }) => {
    setValid({ from: '', to: '', type: '' });
    if (!onValidate) return;
    const result = onValidate(from, to, false, true);
    setValid({
      from: result.startDateHelperText ?? '',
      to: result.endDateHelperText ?? '',
      type: result.error ? 'error' : '',
    });
  };

function findValidComparisonRange(
  desiredFrom: Date,
  mainRangeDuration: number,
  isDisabled: (date: Date) => boolean
): { from: Date; to: Date } {
  const candidateTo = (from: Date) => endOfWeek(addDays(from, mainRangeDuration), WEEKLY_START_DAY_OPTIONS);
  const toWeekIsDisabled = (from: Date) =>
    eachDayOfInterval({
      start: startOfWeek(candidateTo(from), WEEKLY_START_DAY_OPTIONS),
      end: candidateTo(from),
    }).some(isDisabled);

  let from = desiredFrom;
  for (let i = 0; i <= 52; i++) {
    if (!toWeekIsDisabled(from)) {
      return { from, to: candidateTo(from) };
    }
    from = startOfWeek(addWeeks(desiredFrom, -(i + 1)), WEEKLY_START_DAY_OPTIONS);
  }

  // Fallback: return the original desired range (validation will catch it)
  return { from: desiredFrom, to: candidateTo(desiredFrom) };
}

export function useWeekRangeState({
  value,
  onChange,
  onCancel,
  today,
  disabledDates,
  onValidate,
  isComparison = false,
  mainRangeDuration,
  intl,
  hideButtons = false,
}: WeekRangeStateOptions): WeekRangeState {
  const [draft, setDraft] = useState(value);
  const [valid, setValid] = useState<ValidState>({ from: '', to: '', type: '' });
  const [activeDateField, setActiveDateField] = useState<ActiveDateField>({ range: 'range', field: 'start' });
  const [calendarMonth, setCalendarMonth] = useState(() => getInitialMonth(draft.range.from, draft.range.to, today));
  const [weekHoverDate, setWeekHoverDate] = useState<Date | undefined>(undefined);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  useEffect(() => {
    setCalendarMonth(getInitialMonth(draft.range.from, draft.range.to, today));
  }, [draft.range.from, draft.range.to, today]);

  const handleApply = useCallback(() => {
    if (!isValidDate(draft.range.from) || !isValidDate(draft.range.to)) {
      setValid(prev => ({ ...prev, type: 'error' }));
      return;
    }
    if (!valid.from && !valid.to) {
      onChange(draft);
    } else {
      setValid(prev => ({ ...prev, type: 'error' }));
    }
  }, [draft, valid, onChange]);

  const handleCancel = useCallback(() => {
    setDraft(value);
    onCancel();
  }, [value, onCancel]);

  const handlePresetChange = useCallback(
    (from: Date | undefined, to: Date | undefined, presetValue: string, labelKey: string) => {
      const presetLabel = presetValue !== 'custom' ? labelKey : undefined;
      const newDraft = { ...draft, range: { from, to, presetValue, presetLabel } };
      setDraft(newDraft);
      if (hideButtons && presetValue !== 'custom') {
        onChange(newDraft);
      }
    },
    [draft, hideButtons, onChange]
  );

  const weeklyLimitAwareDisabledDates = useCallback(
    (date: Date) => {
      if (activeDateField.field === 'end' && isValidDate(draft.range.from) && !isComparison) {
        return disabledDates(date) || isAfter(date, endOfWeek(addWeeks(draft.range.from, WEEKLY_MAX_RANGE_WEEKS)));
      }
      return disabledDates(date);
    },
    [disabledDates, draft.range.from, activeDateField.field, isComparison]
  );

  const handleDayClick = useCallback(
    (proposedDate: Date) => {
      const weekStart = startOfWeek(proposedDate, WEEKLY_START_DAY_OPTIONS);

      if (isComparison && mainRangeDuration !== undefined) {
        if (weeklyLimitAwareDisabledDates(weekStart)) return;

        const { from: newFrom, to: newTo } = findValidComparisonRange(weekStart, mainRangeDuration, disabledDates);
        setWeekHoverDate(undefined);
        setDraft(prev => ({
          ...prev,
          range: { ...prev.range, from: newFrom, to: newTo, presetValue: 'custom', presetLabel: undefined },
        }));
        return;
      }

      const weekEnd = endOfWeek(proposedDate, WEEKLY_START_DAY_OPTIONS);
      const weekDays = eachDayOfInterval({ start: weekStart, end: weekEnd });
      if (weekDays.some(d => disabledDates(d))) return;

      const validFrom = isValidDate(draft.range.from) ? draft.range.from : undefined;
      const validTo = isValidDate(draft.range.to) ? draft.range.to : undefined;
      const snappedRange = {
        ...draft.range,
        from: validFrom ? startOfWeek(validFrom, WEEKLY_START_DAY_OPTIONS) : undefined,
        to: validTo ? endOfWeek(validTo, WEEKLY_START_DAY_OPTIONS) : undefined,
      };

      const [nextActive, nextRange] = getNextState(activeDateField, snappedRange, '', weekStart);
      const snappedTo = nextRange.to ? endOfWeek(nextRange.to, WEEKLY_START_DAY_OPTIONS) : undefined;

      setWeekHoverDate(undefined);
      setDraft(prev => ({
        ...prev,
        range: { ...nextRange, to: snappedTo, presetValue: 'custom', presetLabel: undefined },
      }));
      setActiveDateField(nextActive);
    },
    [disabledDates, draft.range, activeDateField, isComparison, mainRangeDuration, weeklyLimitAwareDisabledDates]
  );

  const handleDayHover = useCallback(
    (date: Date) => {
      if (weeklyLimitAwareDisabledDates(date)) {
        setWeekHoverDate(undefined);
      } else {
        setWeekHoverDate(date);
      }
    },
    [weeklyLimitAwareDisabledDates]
  );

  const handleCustomInputChange = useCallback(
    (from: Date | undefined, to: Date | undefined) => {
      const snappedFrom = isValidDate(from) ? startOfWeek(from, WEEKLY_START_DAY_OPTIONS) : from;

      if (isComparison && isValidDate(snappedFrom) && mainRangeDuration !== undefined) {
        const { from: newFrom, to: newTo } = findValidComparisonRange(snappedFrom, mainRangeDuration, disabledDates);
        setDraft(prev => ({
          ...prev,
          range: { ...prev.range, from: newFrom, to: newTo, presetValue: 'custom', presetLabel: undefined },
        }));
        return;
      }

      const snappedTo = isValidDate(to) ? endOfWeek(to, WEEKLY_START_DAY_OPTIONS) : to;
      setDraft(prev => ({
        ...prev,
        range: { ...prev.range, from: snappedFrom, to: snappedTo, presetValue: 'custom', presetLabel: undefined },
      }));
    },
    [isComparison, mainRangeDuration, disabledDates]
  );

  const weekHoverModifiers = useMemo<WeekHoverModifiers>(() => {
    const empty: WeekHoverModifiers = { weekHoverMiddle: [], weekHoverEdge: [] };
    if (!weekHoverDate) return empty;

    const hoverWeekStart = startOfWeek(weekHoverDate, WEEKLY_START_DAY_OPTIONS);
    const hoverWeekEnd = endOfWeek(weekHoverDate, WEEKLY_START_DAY_OPTIONS);
    const hoverWeekDays = eachDayOfInterval({ start: hoverWeekStart, end: hoverWeekEnd });

    const from = isValidDate(draft.range.from) ? draft.range.from : undefined;
    const to = isValidDate(draft.range.to) ? draft.range.to : undefined;

    if (!from || to) {
      const confirmedFrom = from ? startOfWeek(from, WEEKLY_START_DAY_OPTIONS) : undefined;
      const confirmedTo = to ? endOfWeek(to, WEEKLY_START_DAY_OPTIONS) : undefined;
      return {
        weekHoverMiddle: hoverWeekDays.filter(
          d =>
            !weeklyLimitAwareDisabledDates(d) &&
            !(confirmedFrom && isSameDay(d, confirmedFrom)) &&
            !(confirmedTo && isSameDay(d, confirmedTo))
        ),
        weekHoverEdge: [],
      };
    }

    const confirmedWeekStart = startOfWeek(from, WEEKLY_START_DAY_OPTIONS);
    const confirmedWeekEnd = endOfWeek(from, WEEKLY_START_DAY_OPTIONS);
    const hoveringSameWeek = isSameDay(hoverWeekStart, confirmedWeekStart);
    const hoveringBefore = isBefore(hoverWeekStart, confirmedWeekStart);

    if (hoveringSameWeek) {
      return {
        weekHoverMiddle: hoverWeekDays.filter(
          d => !weeklyLimitAwareDisabledDates(d) && !isSameDay(d, confirmedWeekStart) && !isSameDay(d, confirmedWeekEnd)
        ),
        weekHoverEdge: [confirmedWeekStart, confirmedWeekEnd].filter(d => !weeklyLimitAwareDisabledDates(d)),
      };
    }

    if (!hoveringBefore) {
      const bridgeDays = eachDayOfInterval({ start: confirmedWeekStart, end: hoverWeekEnd });
      return {
        weekHoverMiddle: bridgeDays.filter(
          d => !weeklyLimitAwareDisabledDates(d) && !isSameDay(d, confirmedWeekStart) && !isSameDay(d, hoverWeekEnd)
        ),
        weekHoverEdge: [hoverWeekEnd].filter(d => !weeklyLimitAwareDisabledDates(d)),
      };
    }

    const bridgeDays = eachDayOfInterval({ start: hoverWeekStart, end: confirmedWeekEnd });
    return {
      weekHoverMiddle: bridgeDays.filter(
        d => !weeklyLimitAwareDisabledDates(d) && !isSameDay(d, hoverWeekStart) && !isSameDay(d, confirmedWeekEnd)
      ),
      weekHoverEdge: [hoverWeekStart, confirmedWeekEnd].filter(d => !weeklyLimitAwareDisabledDates(d)),
    };
  }, [weekHoverDate, draft.range.from, draft.range.to, weeklyLimitAwareDisabledDates]);

  const handleValidate = useCallback(makeValidationHandler(setValid, onValidate, intl), [onValidate, intl]);

  useEffect(() => {
    if (draft.range.from && draft.range.to) {
      handleValidate({ from: draft.range.from, to: draft.range.to });
    } else {
      setValid({ from: '', to: '', type: '' });
    }
  }, [draft.range.from, draft.range.to, handleValidate]);

  return {
    draft,
    valid,
    activeDateField,
    setActiveDateField,
    calendarMonth,
    weekHoverModifiers,
    handleApply,
    handleCancel,
    handlePresetChange,
    handleDayClick,
    handleDayHover,
    handleCustomInputChange,
    weeklyLimitAwareDisabledDates,
  };
}
